import axios, { AxiosError } from 'axios';
import { Mutex } from 'async-mutex';
import { getConfig, isWidgetConfig } from '@creditornot/converse-shared';
import { waitAsync, reconnectAfterMs, isTokenAboutToExpire, mapRawDataToTokenData } from './utils';
import { maxRetryCount } from './constants';
import { postLogout, postToken, postTokenV2 } from './api';
import * as storage from './storage';
// fallback for browsers that don't support Web Locks API and Node.js environments
const authMutex = new Mutex();
const runExclusive = async (callback) => {
    if (!navigator.locks) {
        return authMutex.runExclusive(async () => await callback());
    }
    return navigator.locks.request('converse-auth', async () => await callback());
};
export const createAuthenticator = (getConverseToken) => {
    const config = getConfig();
    const exchangeRefreshToken = async (refreshToken, tries = 1) => {
        try {
            const rawToken = isWidgetConfig(config)
                ? await postTokenV2({
                    grantType: 'refresh_token',
                    audience: config.identityType === 'guest' ? 'converse_widget_guests' : 'converse_widget',
                    refreshToken,
                    appId: config.appId,
                })
                : await postToken({
                    grantType: 'refresh_token',
                    refreshToken,
                    workspaceId: config.workspaceId,
                });
            return mapRawDataToTokenData(rawToken);
        }
        catch (error) {
            if (error instanceof AxiosError) {
                // do not retry if credentials are invalid
                if (error.response?.status === 401) {
                    throw error;
                }
            }
            const nextTry = tries + 1;
            if (nextTry > maxRetryCount) {
                // stop retrying after reaching attempts limit
                throw error;
            }
            await waitAsync(reconnectAfterMs(nextTry));
            return exchangeRefreshToken(refreshToken, nextTry);
        }
    };
    const createToken = async () => {
        const rawData = await getConverseToken();
        // TODO(chat-3549): send get /identity request to get token introspection
        const tokenData = mapRawDataToTokenData(rawData);
        storage.setTokenData(tokenData);
        return tokenData;
    };
    const refreshAccessToken = async () => {
        const { refreshToken } = storage.getTokenData() ?? {};
        if (!refreshToken) {
            // no refresh token available — try to create a new token instead
            return createToken();
        }
        try {
            const tokenData = await exchangeRefreshToken(refreshToken).catch(error => {
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    // 401 here means that refresh token is no longer valid
                    // try to create a new token instead
                    return createToken();
                }
                // re-throw error
                return Promise.reject(error);
            });
            storage.setTokenData(tokenData);
            return tokenData;
        }
        catch (error) {
            storage.removeTokenData();
            // TODO(CHAT-000): call createToken() here instead of throwing an error
            // re-throw error
            throw error;
        }
    };
    const getToken = async () => await runExclusive(async () => {
        let tokenData = storage.getTokenData() || (await createToken());
        if (isTokenAboutToExpire(tokenData)) {
            tokenData = await refreshAccessToken();
        }
        return tokenData.accessToken;
    });
    const maybeRefreshToken = async (expiringAccessToken) => await runExclusive(async () => {
        // check that the token we're trying to refresh is the same as the one in storage
        // otherwise we might be refreshing an already refreshed token
        // if so, just return the token from storage
        const tokenData = storage.getTokenData();
        if (expiringAccessToken &&
            tokenData &&
            tokenData.accessToken !== expiringAccessToken &&
            !isTokenAboutToExpire(tokenData)) {
            return tokenData.accessToken;
        }
        return (await refreshAccessToken()).accessToken;
    });
    const logout = async () => {
        const tokenData = storage.getTokenData();
        if (!tokenData) {
            return;
        }
        // remove token data from storage before sending logout request
        storage.removeTokenData();
        return postLogout(tokenData.refreshToken);
    };
    return {
        getToken,
        maybeRefreshToken,
        logout,
    };
};
let authenticator;
export const initAuthenticator = (getConverseToken) => {
    if (authenticator) {
        // eslint-disable-next-line no-console
        console.warn("Authenticator was already initialized. If you see this message, there's likely a problem in how you setup the SDK.");
    }
    authenticator = createAuthenticator(getConverseToken);
    // cleanup on shutdown
    return () => {
        authenticator = undefined;
    };
};
export const getAuthenticator = () => {
    if (!authenticator) {
        throw new Error('Authenticator is not initialized');
    }
    return authenticator;
};
//# sourceMappingURL=authenticator.js.map